import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Sparkles, Zap, Shield, TrendingDown, ArrowRight, Check,
  Camera, Wand2, Users, Package,
  ChevronRight, Play, ShoppingBag, 
  ArrowLeftRight, Layers, Store, Server,
  Instagram, Clock,
  Briefcase, Plane, Coffee, Wine, Heart,
  Target, Award, ExternalLink, Image, Eye, MousePointer, X
} from 'lucide-react';
import MarketingLayout from '../../components/MarketingLayout';
import SEO, { SEO_CONFIGS } from '../../components/SEO';
import { useSignupConfig } from '../../contexts/SignupConfigContext';
import { HomeHeroVisualDemo, HeroTextSection } from '../../components/HomeHeroVisualDemo';
import { TryOnStudioSection } from '../../components/TryOnStudioSection';

// =============================================================================
// DATA - Images and Content
// =============================================================================

// Multiple hero try-on sets for carousel animation
const heroTryOnSets = [
  {
    id: 1,
    inputHumanUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart-ai/20260118185750.webp",
    inputGarmentUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart-ai/20260118185729.webp",
    outputUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart-ai/20260118185822.webp"
  },
  {
    id: 2,
    inputHumanUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260125062053.webp",
    inputGarmentUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260125062120.webp",
    outputUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260125062142.webp"
  },
  {
    id: 3,
    inputHumanUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260109225743.webp",
    inputGarmentUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260109225814.webp",
    outputUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260109225836.webp"
  },
  {
    id: 4,
    inputHumanUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart-ai/20260118184115.webp",
    inputGarmentUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart-ai/20260118184048.webp",
    outputUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart-ai/20260118183957.webp"
  },
  {
    id: 5,
    inputHumanUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260125061943.webp",
    inputGarmentUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260125062005.webp",
    outputUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260125062023.webp"
  },
  {
    id: 6,
    inputHumanUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260106163131.webp",
    inputGarmentUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260106163202.webp",
    outputUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260106163221.webp"
  }
];

// Keep original for backward compatibility
const heroTryOnData = heroTryOnSets[0];

// =============================================================================
// SLIM INFO RIBBON - Dynamic Signup Credits
// =============================================================================
const SignupCreditsRibbon = () => {
  const { signupCredits, loading } = useSignupConfig();
  
  // Hide ribbon if loading, no credits, or zero credits
  if (loading || !signupCredits || signupCredits <= 0) {
    return null;
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
      className="w-full"
    >
      <div className="flex justify-center lg:justify-start">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border border-amber-200/60 rounded-full text-amber-800 text-xs sm:text-sm group relative overflow-hidden shadow-sm">
          {/* Subtle shimmer effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent -translate-x-full animate-shimmer" style={{ animationDuration: '3s' }} />
          
          <span className="text-base">🎁</span>
          <span className="font-medium relative z-10">
            Introductory Offer: Get <span className="text-amber-900 font-semibold">{signupCredits}</span> free credits on signup.
          </span>
        </div>
      </div>
    </motion.div>
  );
};

const momentsData = [
  { 
    key: "creative_workday", 
    label: "Work", 
    icon: Briefcase, 
    imageUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260107183233.webp",
    tagline: "Confident. Poised. Unstoppable."
  },
  { 
    key: "escape", 
    label: "Travel", 
    icon: Plane, 
    imageUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260107183348.webp",
    tagline: "Adventurous. Free. Photogenic."
  },
  { 
    key: "unwind", 
    label: "Casual", 
    icon: Coffee, 
    imageUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260107183306.webp",
    tagline: "Effortless. Chic. Joyful."
  },
  { 
    key: "after_hours", 
    label: "Evening", 
    icon: Wine, 
    imageUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260107183327.webp",
    tagline: "Elegant. Alluring. Polished."
  },
  { 
    key: "gentle_celebration", 
    label: "Festive", 
    icon: Heart, 
    imageUrl: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260107183406.webp",
    tagline: "Radiant. Graceful. Cherished."
  }
];

// =============================================================================
// HERO SECTION - New Positioning: Catalog to Personal Shopping
// =============================================================================

const HeroSection = () => {
  const [sliderPosition, setSliderPosition] = useState(80);
  const [isHovering, setIsHovering] = useState(false);
  const [currentSetIndex, setCurrentSetIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const containerRef = useRef(null);
  
  const currentSet = heroTryOnSets[currentSetIndex];
  const totalSets = heroTryOnSets.length;

  // Slider animation with set cycling
  useEffect(() => {
    if (!isHovering && !isTransitioning) {
      const interval = setInterval(() => {
        setSliderPosition(prev => {
          // When slider reaches the end (20%), transition to next set
          if (prev <= 20) {
            setIsTransitioning(true);
            return prev;
          }
          return prev - 0.5;
        });
      }, 50);
      return () => clearInterval(interval);
    }
  }, [isHovering, isTransitioning]);

  // Handle set transition
  useEffect(() => {
    if (isTransitioning) {
      const timeout = setTimeout(() => {
        // Move to next set (loop back to 0 if at end)
        setCurrentSetIndex(prev => (prev + 1) % totalSets);
        // Reset slider to start position
        setSliderPosition(80);
        setIsTransitioning(false);
      }, 800); // Transition delay
      return () => clearTimeout(timeout);
    }
  }, [isTransitioning, totalSets]);

  const handleSliderInteraction = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(5, Math.min(95, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  // Manual navigation
  const goToSet = (index) => {
    setCurrentSetIndex(index);
    setSliderPosition(80);
  };

  return (
    <section className="pt-20 sm:pt-24 pb-12 sm:pb-16 bg-white">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left - Content */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="order-1 lg:order-1"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-5 leading-tight text-slate-900">
              Sell More. Return Less.
            </h1>
            
            <p className="text-lg sm:text-xl text-slate-600 mb-8 leading-relaxed max-w-lg">
              The conversion & return optimization engine built for fashion brands.
            </p>
            
            {/* CTA and Ribbon - Hidden on mobile, shown on desktop */}
            <div className="hidden lg:block">
              <div className="flex flex-col sm:flex-row gap-4 mb-4">
                <Link 
                  to="/partners/login" 
                  className="px-10 py-4 bg-blue-600 text-white rounded-xl text-lg font-semibold hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-xl shadow-blue-600/30 hover:shadow-blue-600/40 hover:scale-[1.02]"
                >
                  Get Started
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a
                  href="https://nanokart.ai/5f0f23fa-9f83-44ef-b73c-e2ecb15c2326/customers/tryon"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-10 py-4 bg-white border-2 border-slate-200 hover:border-blue-300 text-slate-800 hover:text-blue-700 rounded-xl text-lg font-semibold hover:bg-blue-50 transition-all flex items-center justify-center gap-2 hover:scale-[1.02]"
                  data-testid="try-live-demo-btn"
                >
                  <Sparkles className="w-5 h-5 text-blue-500" />
                  Try Live Demo
                </a>
              </div>
              <SignupCreditsRibbon />
            </div>
          </motion.div>

          {/* Right - Interactive Slider Carousel */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-4 sm:p-6 border border-slate-200 order-2 lg:order-2"
          >
            <div 
              ref={containerRef}
              className="relative w-full aspect-[3/4] rounded-xl overflow-hidden cursor-ew-resize select-none bg-slate-100"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
              onMouseMove={(e) => isHovering && handleSliderInteraction(e.clientX)}
              onTouchMove={(e) => handleSliderInteraction(e.touches[0].clientX)}
            >
              {/* Base layer: After (Try-On result) with fade transition */}
              <motion.img 
                key={`after-${currentSet.id}`}
                src={currentSet.outputUrl}
                alt="AI Virtual Try-On result showing the garment on customer - After"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ objectPosition: 'center top' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: isTransitioning ? 0 : 1 }}
                transition={{ duration: 0.4 }}
                draggable={false}
                width={600}
                height={800}
                fetchpriority="high"
              />
              {/* Overlay: Before (Original) - clips from right based on slider */}
              <motion.div 
                className="absolute inset-0 overflow-hidden"
                style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
                initial={{ opacity: 0 }}
                animate={{ opacity: isTransitioning ? 0 : 1 }}
                transition={{ duration: 0.4 }}
              >
                <img 
                  key={`before-${currentSet.id}`}
                  src={currentSet.inputHumanUrl}
                  alt="Customer photo before virtual try-on - Before"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ objectPosition: 'center top' }}
                  draggable={false}
                  width={600}
                  height={800}
                  loading="eager"
                  decoding="async"
                />
              </motion.div>
              {/* Slider handle */}
              <motion.div 
                className="absolute top-0 bottom-0 z-20"
                style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
                animate={{ opacity: isTransitioning ? 0 : 1 }}
                transition={{ duration: 0.2 }}
              >
                <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-0.5 bg-white shadow-lg" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center border-2 border-slate-200">
                  <ArrowLeftRight className="w-5 h-5 text-slate-600" />
                </div>
              </motion.div>
              {/* Labels */}
              <motion.div 
                className="absolute bottom-3 left-3 px-2 py-1 bg-slate-900/80 rounded text-white text-xs font-medium"
                animate={{ opacity: isTransitioning ? 0 : 1 }}
              >
                BEFORE
              </motion.div>
              <motion.div 
                className="absolute bottom-3 right-3 px-2 py-1 bg-blue-600 rounded text-white text-xs font-medium"
                animate={{ opacity: isTransitioning ? 0 : 1 }}
              >
                AFTER
              </motion.div>
              
              {/* Garment Preview - Bottom Left Corner */}
              <motion.div 
                key={`garment-overlay-${currentSet.id}`}
                className="absolute bottom-14 left-3 w-20 h-24 sm:w-24 sm:h-28 rounded-lg overflow-hidden border-2 border-white bg-white shadow-lg z-10"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: isTransitioning ? 0 : 1 }}
                transition={{ duration: 0.3 }}
              >
                <img 
                  src={currentSet.inputGarmentUrl} 
                  alt="Dress being tried on" 
                  className="w-full h-full object-cover"
                  width={96}
                  height={112}
                  loading="eager"
                  decoding="async"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-1.5 py-1">
                  <p className="text-[9px] text-white font-medium text-center">Dress</p>
                </div>
              </motion.div>
              
              {/* Transition overlay */}
              {isTransitioning && (
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-r from-blue-100 to-purple-100 flex items-center justify-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="text-center">
                    <Sparkles className="w-8 h-8 text-blue-500 mx-auto mb-2 animate-pulse" />
                    <p className="text-sm text-slate-600">Loading next look...</p>
                  </div>
                </motion.div>
              )}
            </div>
            
            {/* Bottom section with carousel dots */}
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div>
                  <p className="text-sm font-medium text-slate-700">See it on you</p>
                  <p className="text-xs text-slate-500">Before you buy</p>
                </div>
              </div>
              
              {/* Carousel dots */}
              <div className="flex items-center gap-2">
                {heroTryOnSets.map((set, index) => (
                  <button
                    key={set.id}
                    onClick={() => goToSet(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentSetIndex 
                        ? 'w-6 bg-blue-600' 
                        : 'bg-slate-300 hover:bg-slate-400'
                    }`}
                    aria-label={`Go to look ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>

          {/* Mobile CTA - Below image on mobile only */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="lg:hidden order-3 text-center"
          >
            <div className="flex flex-col gap-4">
              <Link 
                to="/partners/login" 
                className="px-10 py-4 bg-blue-600 text-white rounded-xl text-lg font-semibold hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-xl shadow-blue-600/30 hover:shadow-blue-600/40 hover:scale-[1.02]"
              >
                Get Started
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="https://nanokart.ai/5f0f23fa-9f83-44ef-b73c-e2ecb15c2326/customers/tryon"
                target="_blank"
                rel="noopener noreferrer"
                className="px-10 py-4 bg-white border-2 border-slate-200 hover:border-blue-300 text-slate-800 hover:text-blue-700 rounded-xl text-lg font-semibold hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
                data-testid="try-live-demo-btn-mobile"
              >
                <Sparkles className="w-5 h-5 text-blue-500" />
                Try Live Demo
              </a>
            </div>
            <div className="mt-4">
              <SignupCreditsRibbon />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// WHY CATALOG SHOPPING IS BROKEN - New Section
// =============================================================================

const WhyCatalogBrokenSection = () => {
  return (
    <section className="py-16 sm:py-20 bg-slate-50">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
            Why Online Fashion Shopping Is Broken
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Confidence before checkout leads to satisfaction after delivery.
          </p>
        </motion.div>

        {/* Stats Row */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
          {[
            { stat: '30-40%', label: 'Fashion return rate in India', desc: 'Size and fit uncertainty drives returns', color: 'red' },
            { stat: '₹800-₹1,600', label: 'Cost per return', desc: 'Shipping, handling, restocking', color: 'amber' },
            { stat: 'Up to 40%', label: 'Fewer returns with VTO', desc: 'Visualization creates certainty', color: 'emerald' }
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-6 border border-slate-200 text-center"
            >
              <p className={`text-3xl font-bold mb-2 ${
                item.color === 'red' ? 'text-red-600' :
                item.color === 'amber' ? 'text-amber-600' : 'text-emerald-600'
              }`}>{item.stat}</p>
              <p className="font-medium text-slate-900 mb-1">{item.label}</p>
              <p className="text-sm text-slate-500">{item.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Comparison Cards */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Traditional Catalog Shopping */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }} 
            whileInView={{ opacity: 1, x: 0 }} 
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-6 border border-slate-200 relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 right-0 h-1 bg-red-500" />
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <Image className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Traditional Catalog Shopping</h3>
            </div>
            <ul className="space-y-3">
              {[
                { icon: Image, text: 'Flat product images' },
                { icon: Users, text: 'One model for all body types' },
                { icon: MousePointer, text: 'Customers guess size & fit' },
                { icon: ShoppingBag, text: 'Multiple sizes ordered' },
                { icon: X, text: 'High returns, low confidence' }
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-4 h-4 text-red-400" />
                  </div>
                  <span className="text-slate-600">{item.text}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* With NanoKart.ai */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }} 
            whileInView={{ opacity: 1, x: 0 }} 
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-6 border border-slate-200 relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 right-0 h-1 bg-emerald-500" />
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">With NanoKart.ai</h3>
            </div>
            <ul className="space-y-3">
              {[
                { icon: Eye, text: 'Personalized try-on visualization' },
                { icon: Users, text: 'Realistic fit on customer photos' },
                { icon: Check, text: 'One confident size choice' },
                { icon: Zap, text: 'Faster purchase decisions' },
                { icon: TrendingDown, text: 'Fewer returns, better margins' }
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-4 h-4 text-emerald-600" />
                  </div>
                  <span className="text-slate-700 font-medium">{item.text}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Academic Citation */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }}
          className="mt-10 text-center"
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-2 px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm">
            <span className="text-slate-500">Source:</span>
            <span className="text-slate-700">International Journal of Management and Humanities (IJMH)</span>
            <a 
              href="https://www.ijmh.org/wp-content/uploads/papers/v10i2/B16571010223.pdf" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1"
            >
              View research paper <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// =============================================================================
// MOMENTS SECTION - Emotional Confidence (Dark Theme)
// =============================================================================

const MomentsSection = () => {
  const [selectedMoment, setSelectedMoment] = useState(momentsData[0].key);
  const [isPaused, setIsPaused] = useState(false);
  const currentMoment = momentsData.find(m => m.key === selectedMoment) || momentsData[0];

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setSelectedMoment(prev => {
        const idx = momentsData.findIndex(m => m.key === prev);
        return momentsData[(idx + 1) % momentsData.length].key;
      });
    }, 3500);
    return () => clearInterval(interval);
  }, [isPaused]);

  return (
    <section id="moments" className="py-16 sm:py-24 bg-white overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left - Image Showcase */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative max-w-md mx-auto lg:max-w-none order-2 lg:order-1"
          >
            <div className="relative aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl bg-slate-100">
              <motion.img
                key={currentMoment.key}
                src={currentMoment.imageUrl}
                alt={currentMoment.label}
                className="w-full h-full object-cover"
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                width={400}
                height={533}
                loading="lazy"
                decoding="async"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 sm:p-6">
                <p className="text-white text-base sm:text-lg font-medium">{currentMoment.tagline}</p>
                <p className="text-white/60 text-xs sm:text-sm mt-1">&ldquo;{currentMoment.label}&rdquo; moment via Moments API</p>
              </div>
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl blur-xl -z-10" />
            </div>
            <div className="flex gap-1.5 justify-center mt-5">
              {momentsData.map((m) => (
                <div 
                  key={m.key}
                  className={`h-1.5 rounded-full transition-all ${
                    m.key === selectedMoment ? 'w-8 bg-gradient-to-r from-purple-600 to-pink-600' : 'w-2 bg-slate-300'
                  }`}
                />
              ))}
            </div>
          </motion.div>

          {/* Right - Copy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center lg:text-left order-1 lg:order-2"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4 leading-tight">
              Beyond Fit.{" "}
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Visualize the Moment.</span>
            </h2>
            
            <p className="text-base sm:text-lg text-slate-600 mb-6">
              Let customers see themselves in the moments that matter - before they commit.
            </p>

            {/* Distinction callout - Enhanced UX - Mobile Responsive */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6">
              {/* Try-On Box */}
              <div className="flex-1 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-xl relative overflow-hidden group hover:bg-blue-100/50 transition-all">
                <div className="absolute top-0 right-0 w-16 h-16 bg-blue-100 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Camera className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-[10px] text-blue-600 uppercase tracking-widest font-medium">TRY-ON</p>
                    <p className="text-slate-900 text-lg font-bold">Fit</p>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2 text-left">Does this fit me?</p>
              </div>

              {/* Arrow connector */}
              <div className="flex sm:flex-col items-center justify-center gap-1 py-1 sm:py-0">
                <ArrowRight className="w-5 h-5 text-slate-400 rotate-90 sm:rotate-0" />
                <span className="text-[9px] text-slate-400 uppercase tracking-wider">Then</span>
              </div>

              {/* Moments Box */}
              <div className="flex-1 p-3 sm:p-4 bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl relative overflow-hidden group hover:from-purple-100/50 hover:to-pink-100/50 transition-all">
                <div className="absolute top-0 right-0 w-16 h-16 bg-purple-100 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-12 h-12 bg-pink-100 rounded-full translate-y-1/2 -translate-x-1/2" />
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-[10px] text-purple-600 uppercase tracking-widest font-medium">MOMENTS</p>
                    <p className="text-slate-900 text-lg font-bold">Feeling</p>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2 text-left">Does this fit my life?</p>
              </div>
            </div>

            {/* Moment Selector */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-2">
              {momentsData.map((moment) => {
                const Icon = moment.icon;
                const isSelected = selectedMoment === moment.key;
                return (
                  <button
                    key={moment.key}
                    onClick={() => { setSelectedMoment(moment.key); setIsPaused(true); setTimeout(() => setIsPaused(false), 6000); }}
                    className={`flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl text-sm font-medium transition-all ${
                      isSelected 
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/30' 
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {moment.label}
                  </button>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// RETURNS SECTION - Outcome-Based (Confidence leads to lower returns)
// =============================================================================

const ReturnsSectionOutcome = () => {
  return (
    <section className="py-16 sm:py-20 bg-slate-50">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
            When Customers See Themselves Clearly, Returns Drop Naturally
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Confidence before checkout leads to satisfaction after delivery.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {[
            { 
              stat: '30-40%', 
              label: 'Fashion return rate in India', 
              desc: 'Size and fit uncertainty drives returns',
              color: 'red'
            },
            { 
              stat: '₹800-₹1,600', 
              label: 'Cost per return', 
              desc: 'Shipping, handling, restocking',
              color: 'amber'
            },
            { 
              stat: 'Up to 40%', 
              label: 'Fewer returns with VTO', 
              desc: 'Visualization creates certainty',
              color: 'emerald'
            }
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-6 border border-slate-200 text-center"
            >
              <p className={`text-3xl font-bold mb-2 ${
                item.color === 'red' ? 'text-red-600' :
                item.color === 'amber' ? 'text-amber-600' : 'text-emerald-600'
              }`}>{item.stat}</p>
              <p className="font-medium text-slate-900 mb-1">{item.label}</p>
              <p className="text-sm text-slate-500">{item.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Academic Citation */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }}
          className="mt-10 text-center"
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-2 px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm">
            <span className="text-slate-500">Source:</span>
            <span className="text-slate-700">International Journal of Management and Humanities (IJMH)</span>
            <a 
              href="https://www.ijmh.org/wp-content/uploads/papers/v10i2/B16571010223.pdf" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1"
            >
              View research paper <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// =============================================================================
// HOW TO USE NANOKART.AI - Comprehensive Section
// =============================================================================

const HowToUseSection = () => {
  return (
    <section className="py-16 sm:py-24 bg-gradient-to-b from-slate-50 to-white" id="how-it-works">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-3">
            Who is NanoKart.ai for?
          </h2>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            One AI platform. Multiple ways to sell fashion: online, social, and in-store.
          </p>
        </motion.div>

        {/* Section 1: D2C Brands & Marketplaces */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12 max-w-6xl mx-auto mb-16 sm:mb-24"
        >
          {/* Left Side - Text */}
          <div className="flex-1 text-center lg:text-left">
            <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full mb-4">
              For D2C Brands & Marketplaces
            </span>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight">
              Bring TryOn Studio<sup className="text-[10px] align-super">™</sup> to Your Website
            </h3>
            <p className="mt-3 text-base text-slate-500 leading-relaxed">
              Integrate in minutes with Shopify or API - or simply paste your product link to auto-create your try-on catalog.
            </p>
          </div>
          
          {/* Right Side - Image */}
          <div className="flex-1 w-full max-w-lg lg:max-w-none">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart-ai/20260128181953.webp" 
                alt="D2C Brand Virtual Try-On Integration"
                loading="lazy"
                decoding="async"
                width={800}
                height={600}
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </motion.div>

        {/* Section 2: Instagram & WhatsApp Sellers */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col lg:flex-row-reverse items-center gap-8 lg:gap-12 max-w-6xl mx-auto mb-16 sm:mb-24"
        >
          {/* Left Side - Text (appears on right on desktop due to flex-row-reverse) */}
          <div className="flex-1 text-center lg:text-left">
            <span className="inline-block px-4 py-1.5 bg-pink-100 text-pink-700 text-sm font-semibold rounded-full mb-4">
              For Instagram & WhatsApp Sellers
            </span>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight">
              Let Customers Try at Home - Then Buy Instantly
            </h3>
            <p className="mt-3 text-base text-slate-500 leading-relaxed">
              Turn every post into a fitting room with dedicated try-on links for each product.
            </p>
          </div>
          
          {/* Right Side - Image (appears on left on desktop) */}
          <div className="flex-1 w-full max-w-lg lg:max-w-none">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart-ai/20260128182520.webp" 
                alt="Instagram and WhatsApp Virtual Try-On for Social Sellers"
                loading="lazy"
                decoding="async"
                width={800}
                height={600}
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </motion.div>

        {/* Section 3: Boutiques & Retail Stores */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12 max-w-6xl mx-auto mb-16 sm:mb-24"
        >
          {/* Left Side - Text */}
          <div className="flex-1 text-center lg:text-left">
            <span className="inline-block px-4 py-1.5 bg-emerald-100 text-emerald-700 text-sm font-semibold rounded-full mb-4">
              For Boutiques & Retail Stores
            </span>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight">
              Convert More Walk-Ins with TryOn Studio<sup className="text-[10px] align-super">™</sup>
            </h3>
            <p className="mt-3 text-base text-slate-500 leading-relaxed">
              No hardware needed. Generate QR codes instantly so customers can try your full catalog on their own phones.
            </p>
          </div>
          
          {/* Right Side - Image */}
          <div className="flex-1 w-full max-w-lg lg:max-w-none">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart-ai/20260128183117.webp" 
                alt="Boutique and Retail Store Virtual Try-On Experience"
                loading="lazy"
                decoding="async"
                width={800}
                height={600}
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </motion.div>

        {/* Closing Message */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }}
          className="mt-12 sm:mt-16 text-center max-w-3xl mx-auto"
        >
          <div className="bg-gradient-to-br from-blue-50 via-white to-purple-50 rounded-2xl p-6 sm:p-8 border border-slate-200 shadow-sm">
            <p className="text-lg sm:text-xl text-slate-800 font-semibold mb-2 leading-relaxed">
              One platform. Every channel. One consistent try-on experience.
            </p>
            <p className="text-sm sm:text-base text-blue-600 font-medium">
              NanoKart.ai enables fashion brands to deliver a consistent, high-confidence try-on experience across every sales channel, from online to in-store.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// =============================================================================
// WHY DIFFERENT - Dark Theme
// =============================================================================

// =============================================================================
// ROI TEASER SECTION
// =============================================================================

const ROITeaserSection = () => (
  <section className="py-14 sm:py-16 bg-white">
    <div className="container mx-auto px-4 sm:px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="max-w-2xl mx-auto text-center"
      >
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 leading-tight">
          Run the Numbers. See the Upside.
        </h2>
        <p className="text-base sm:text-lg text-slate-500 leading-relaxed mb-8">
          Instantly estimate revenue uplift and return savings - based on real D2C benchmarks.
        </p>
        <a
          href="/why-nanokart#roi-calculator"
          className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 text-white rounded-full font-semibold text-base hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 hover:scale-[1.02]"
          data-testid="roi-calculator-link"
        >
          Calculate My Revenue Impact
        </a>
        <p className="mt-4 text-sm text-slate-400">Takes less than 30 seconds</p>
      </motion.div>
    </div>
  </section>
);

// =============================================================================
const WhyDifferentSection = () => {
  const points = [
    { icon: Layers, title: 'Natural Drape', desc: 'Not flat overlay. Real fabric physics that respond to body shape and posture.' },
    { icon: Users, title: 'Made for India', desc: 'Trained on Indian body types, fabrics, and lighting conditions.' },
    { icon: Target, title: 'Sales-Ready Output', desc: 'Brand-safe, high-resolution images perfect for PDPs, ads, and social media.' },
    { icon: Server, title: 'Production APIs', desc: 'Enterprise-grade with smart caching, analytics dashboards, and SLAs.' }
  ];

  return (
    <section className="py-16 sm:py-20 bg-gradient-to-br from-slate-900 to-slate-800">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          {/* DPIIT Recognition Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/20 border border-emerald-400/30 rounded-full text-emerald-300 text-sm mb-4">
            <span>🏷️</span>
            <span className="font-medium">DPIIT Recognized Startup</span>
          </div>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">
            Why NanoKart.ai Is Different
          </h2>
          <p className="text-slate-400 max-w-xl mx-auto">Built from the ground up for fashion commerce, not retrofitted from generic AI.</p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {points.map((p, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl p-6 hover:border-blue-500/50 transition-all"
            >
              <div className="w-12 h-12 mb-4 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-400/30 flex items-center justify-center">
                <p.icon className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="font-semibold text-white text-lg mb-2">{p.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// SMARTGATE SECTION
// =============================================================================

const SmartGateSection = () => {
  const features = [
    {
      number: '01',
      title: 'Stop Window-Shopper Drain',
      desc: 'Prevent unnecessary AI usage with a smart, customizable try-on wallet for every visitor.',
    },
    {
      number: '02',
      title: 'Turn Usage Into Loyalty',
      desc: 'Reward customers with credits for purchases, referrals, and sign-ups - driving repeat engagement.',
    },
    {
      number: '03',
      title: '100% Free. Fully in Your Control',
      desc: 'Set limits, define rules, and manage budgets - at zero additional cost.',
    },
  ];

  return (
    <section className="py-16 sm:py-24 bg-slate-50">
      <div className="container mx-auto px-4 sm:px-6">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16 max-w-2xl mx-auto"
        >
          {/* Badge */}
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full mb-5 tracking-wide uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
            Included. No extra cost.
          </span>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-3 leading-tight">
            Predictable AI. Zero Compute Waste.
          </h2>
          <p className="text-base sm:text-lg text-slate-500 leading-relaxed">
            SmartGate<sup className="text-[10px] align-super">™</sup> - built-in cost protection and loyalty engine for AI commerce.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid sm:grid-cols-3 gap-5 max-w-4xl mx-auto">
          {features.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.12, duration: 0.5 }}
              viewport={{ once: true }}
              className="relative bg-white rounded-2xl border border-slate-200 p-6 hover:border-blue-300 hover:shadow-md transition-all duration-300 group overflow-hidden"
            >
              {/* Visible number */}
              <span className="block text-4xl font-black text-slate-400 group-hover:text-blue-400 transition-colors leading-none mb-4 select-none">
                {f.number}
              </span>
              <h3 className="font-semibold text-slate-900 text-base mb-2 leading-snug">
                {f.title}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                {f.desc}
              </p>
              {/* Bottom accent line on hover */}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
            </motion.div>
          ))}
        </div>

        {/* Footer line */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-10 text-sm text-slate-400 font-medium tracking-wide"
        >
          You define the rules. We ensure it scales profitably.
        </motion.p>

      </div>
    </section>
  );
};

// =============================================================================
// UNIFYING STATEMENT
// =============================================================================

const UnifyingStatement = () => {
  return (
    <section className="py-12 sm:py-16 bg-white">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto"
        >
          <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight">
            NanoKart.ai isn&apos;t just reducing returns.
            <span className="block text-blue-600 mt-2">It&apos;s redefining how fashion is bought online.</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// =============================================================================
// FINAL CTA
// =============================================================================

const FinalCTASection = () => {
  const { signupCredits } = useSignupConfig();
  
  return (
    <section className="py-16 sm:py-20 bg-blue-600">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }} 
          whileInView={{ opacity: 1, scale: 1 }} 
          viewport={{ once: true }} 
          className="text-center max-w-2xl mx-auto"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
            Ready to Turn Browsers Into Buyers?
          </h2>
          <p className="text-lg text-blue-100 mb-8">
            Start with 500 free credits. No commitment.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a 
              href="/pricing#roi-calculator"
              className="w-full sm:w-auto px-8 py-3.5 bg-white text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 shadow-lg"
            >
              ROI Calculator
            </a>
            <Link 
              to="/partners/register"
              className="w-full sm:w-auto px-8 py-3.5 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-400 transition-colors"
            >
              Start Free Trial
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// =============================================================================
// MAIN HOME COMPONENT - New Narrative Flow
// =============================================================================

const Home = () => {
  return (
    <MarketingLayout lightTheme>
      <SEO {...SEO_CONFIGS.home} />
      
      {/* 1. New Visual Demo Hero - Full screen, no text */}
      <HomeHeroVisualDemo />
      
      {/* 2. Hero Text Section - Below fold */}
      <HeroTextSection />
      
      {/* 3. TryOn Studio - AI-Powered Fashion Suite */}
      <TryOnStudioSection />
      
      {/* 4. How to Use NanoKart.ai - Comprehensive Guide */}
      <HowToUseSection />
      
      {/* 5. Why Online Fashion Shopping Is Broken - Stats + Comparison */}
      <WhyCatalogBrokenSection />

      {/* 5b. ROI Teaser */}
      <ROITeaserSection />

      {/* 6. Why Different */}
      <WhyDifferentSection />
      
      {/* 7. SmartGate - Cost Control & Engagement */}
      <SmartGateSection />

      {/* 8. Unifying Statement */}
      <UnifyingStatement />
      
      {/* 9. Final CTA */}
      <FinalCTASection />
      
      {/* Footer is in MarketingLayout */}
    </MarketingLayout>
  );
};

export default Home;
